import { deriveTitle, extractTags, redactSecrets } from '../data/markdown.ts';
import type { LocalNote } from '../data/types.ts';

/**
 * Vorläufige Suche für Phase 2: Volltext über Titel und Inhalt, dazu ein paar Filter.
 * Phase 4 ersetzt sie durch SQLite/WASM mit FTS5 und Trigram-Index — die Aufrufstelle
 * bleibt dieselbe.
 */

export type Query = {
  terms: string[];
  tags: string[];
  pinnedOnly: boolean;
  /** `in:archiv` und `in:notizen` grenzen auf eine der beiden Sorten ein. */
  only: 'archiv' | 'notizen' | null;
};

export function parseQuery(input: string): Query {
  const terms: string[] = [];
  const tags: string[] = [];
  let pinnedOnly = false;
  let only: Query['only'] = null;

  for (const token of input.trim().split(/\s+/).filter(Boolean)) {
    const lower = token.toLowerCase();
    if (lower.startsWith('tag:')) tags.push(lower.slice(4));
    else if (lower === 'is:pinned') pinnedOnly = true;
    else if (lower === 'in:archiv' || lower === 'in:posteingang') only = 'archiv';
    else if (lower === 'in:notizen') only = 'notizen';
    else terms.push(lower);
  }
  return { terms, tags, pinnedOnly, only };
}

export function matches(note: LocalNote, query: Query): boolean {
  if (query.pinnedOnly && !note.pinned) return false;
  if (query.tags.length > 0) {
    const noteTags = extractTags(note.content);
    if (!query.tags.every((tag) => noteTags.some((candidate) => candidate.includes(tag)))) {
      return false;
    }
  }
  if (query.terms.length === 0) return true;

  // secret-Blöcke bleiben außen vor: Kennwörter sollen nicht über die Suche auffindbar sein.
  // Bei einem Dokument zählt der gewonnene Text mit — er ist der eigentliche Inhalt.
  const haystack =
    `${deriveTitle(note.content)}\n${redactSecrets(note.content)}\n${note.dokument?.erkannt ?? ''}`.toLowerCase();
  return query.terms.every((term) => haystack.includes(term));
}

export function score(note: LocalNote, query: Query): number {
  if (query.terms.length === 0) return 0;
  const title = deriveTitle(note.content).toLowerCase();
  let value = 0;
  for (const term of query.terms) {
    if (title.includes(term)) value += 3;
    if (title.startsWith(term)) value += 2;
  }
  if (note.pinned) value += 1;
  return value;
}
